/**
 * Deterministic mock seeds for B1.10 DayCloseWizard.
 *
 * Simulates a realistic branch day with mild variance. Numbers derived
 * from the B1.7 Cashier seeds so that an end-to-end demo flow tells a
 * coherent story (same orders close out here).
 */

import type {
  DayClosePrerequisite,
  DayCloseSnapshot,
  NonCashTenderCount,
  VarianceTolerancePolicy,
} from './types';
import { TRY_DENOMINATIONS } from './types';

/**
 * Tolerance policy per Lovable contract — three ceilings:
 *   - cashMinor: absolute ceiling for the cash tender (₺10)
 *   - perTenderMinor: per non-cash tender ceiling (₺5)
 *   - hardBlockMinor: owner-only override trigger (₺500)
 *
 * Feature-side the richer backend policy will be derived from the tenant's
 * finance_policies row; here the flat shape mirrors the UI contract.
 */
export const DEFAULT_TOLERANCE_POLICY: VarianceTolerancePolicy = {
  cashMinor: 1000,
  perTenderMinor: 500,
  hardBlockMinor: 50000,
};

const BRANCH_ID = 'branch-patiland-kadikoy';
const BRANCH_NAME = 'PatiLand Kadıköy';
const BUSINESS_DATE = '2026-04-14';
const CASHIER_SESSION_ID = 'sess-2026-04-14-001';

const PREREQUISITES_ALL_GREEN: readonly DayClosePrerequisite[] = [
  {
    key: 'open_orders',
    label: 'Açık Siparişler',
    status: 'ok',
    detail: 'Tüm siparişler kapatılmış.',
    count: 0,
    actionRoute: null,
  },
  {
    key: 'parked_orders',
    label: 'Beklemedeki Siparişler',
    status: 'ok',
    detail: 'Parked sipariş yok.',
    count: 0,
    actionRoute: null,
  },
  {
    key: 'pending_card_capture',
    label: 'Bekleyen Kart Çekimleri',
    status: 'ok',
    detail: 'Tüm kart işlemleri capture edildi.',
    count: 0,
    actionRoute: null,
  },
  {
    key: 'clocked_in_staff',
    label: 'Vardiyada Personel',
    status: 'ok',
    detail: 'Tüm personel mesaiden çıkış yaptı.',
    count: 0,
    actionRoute: null,
  },
  {
    key: 'unadjusted_tips',
    label: 'Düzenlenmemiş Bahşişler',
    status: 'ok',
    detail: 'Bahşiş girişleri tamam.',
    count: 0,
    actionRoute: null,
  },
];

const PREREQUISITES_WITH_WARNINGS: readonly DayClosePrerequisite[] = [
  {
    key: 'open_orders',
    label: 'Açık Siparişler',
    status: 'blocked',
    detail: '2 sipariş henüz kapatılmamış. Devam etmeden önce tamamlayın.',
    count: 2,
    actionRoute: '/cashier',
  },
  {
    key: 'parked_orders',
    label: 'Beklemedeki Siparişler',
    status: 'warning',
    detail: '1 parked sipariş var. Yarın devredilebilir.',
    count: 1,
    actionRoute: '/cashier',
  },
  {
    key: 'pending_card_capture',
    label: 'Bekleyen Kart Çekimleri',
    status: 'ok',
    detail: 'Tüm kart işlemleri capture edildi.',
    count: 0,
    actionRoute: null,
  },
  {
    key: 'clocked_in_staff',
    label: 'Vardiyada Personel',
    status: 'warning',
    detail: '1 personel henüz çıkış yapmadı.',
    count: 1,
    actionRoute: '/staff',
  },
  {
    key: 'unadjusted_tips',
    label: 'Düzenlenmemiş Bahşişler',
    status: 'ok',
    detail: 'Bahşiş girişleri tamam.',
    count: 0,
    actionRoute: null,
  },
];

const EXPECTED_CASH_MINOR = 187550;       // ₺1,875.50
const OPENING_FLOAT_MINOR = 50000;        // ₺500
const EXPECTED_CARD_MINOR = 462300;       // ₺4,623.00
const EXPECTED_GIFT_CARD_MINOR = 25000;   // ₺250
const EXPECTED_TRANSFER_MINOR = 15000;    // ₺150
const EXPECTED_ON_ACCOUNT_MINOR = 8000;   // ₺80

/**
 * Pre-filled demo denomination counts that produce a small ₺7.50
 * variance — within default tolerance, no override required.
 */
const SEED_DENOMINATION_COUNTS = [
  { code: 'TRY_200', count: 3 },   // 3 × ₺200 = ₺600
  { code: 'TRY_100', count: 5 },   // 5 × ₺100 = ₺500
  { code: 'TRY_50', count: 8 },    // 8 × ₺50 = ₺400
  { code: 'TRY_20', count: 10 },   // 10 × ₺20 = ₺200
  { code: 'TRY_10', count: 12 },   // 12 × ₺10 = ₺120
  { code: 'TRY_5', count: 9 },     // 9 × ₺5 = ₺45
  { code: 'TRY_1', count: 7 },     // 7 × ₺1 = ₺7
  { code: 'TRY_050', count: 0 },
  { code: 'TRY_025', count: 0 },
  { code: 'TRY_010', count: 0 },
  { code: 'TRY_005', count: 0 },
  { code: 'TRY_001', count: 0 },
] as const;

const NON_CASH_COUNTS: readonly NonCashTenderCount[] = [
  {
    method: 'card',
    expectedMinor: EXPECTED_CARD_MINOR,
    countedMinor: EXPECTED_CARD_MINOR,
    transactionCount: 14,
    referenceSlip: 'IYZ-BATCH-20260414-001',
  },
  {
    method: 'gift_card',
    expectedMinor: EXPECTED_GIFT_CARD_MINOR,
    countedMinor: EXPECTED_GIFT_CARD_MINOR,
    transactionCount: 2,
    referenceSlip: null,
  },
  {
    method: 'transfer',
    expectedMinor: EXPECTED_TRANSFER_MINOR,
    countedMinor: EXPECTED_TRANSFER_MINOR,
    transactionCount: 1,
    referenceSlip: 'TRX-20260414-001',
  },
  {
    method: 'on_account',
    expectedMinor: EXPECTED_ON_ACCOUNT_MINOR,
    countedMinor: EXPECTED_ON_ACCOUNT_MINOR,
    transactionCount: 1,
    referenceSlip: null,
  },
];

/**
 * Snapshot variant selector — `PREREQ_BLOCKED` for demo of step-1 gates,
 * default is clean flow (prerequisites all green, ready for cash count).
 */
export type SeedScenario = 'DEFAULT' | 'PREREQ_BLOCKED';

function buildSnapshot(scenario: SeedScenario): DayCloseSnapshot {
  const prereqs =
    scenario === 'PREREQ_BLOCKED'
      ? PREREQUISITES_WITH_WARNINGS
      : PREREQUISITES_ALL_GREEN;

  return {
    sessionId: `dc-${BUSINESS_DATE}-001`,
    branchId: BRANCH_ID,
    branchName: BRANCH_NAME,
    businessDate: BUSINESS_DATE,
    cashierSessionId: CASHIER_SESSION_ID,
    state: scenario === 'PREREQ_BLOCKED' ? 'draft' : 'counting',
    currentStep: scenario === 'PREREQ_BLOCKED' ? 'prerequisites' : 'cash',
    version: 1,
    openedByName: 'Zeynep Kaya',
    openedAt: '2026-04-14T19:00:00+03:00',
    submittedAt: null,
    closedAt: null,
    prerequisites: prereqs,
    cash: {
      openingFloatMinor: OPENING_FLOAT_MINOR,
      expectedCashMinor: EXPECTED_CASH_MINOR,
      countedCashMinor: 0,
      denominationCounts: TRY_DENOMINATIONS.map((d) => ({
        code: d.code,
        count: 0,
      })),
    },
    nonCashTenders: NON_CASH_COUNTS,
    variance: {
      rows: [],
      totalAbsMinor: 0,
      withinTolerance: true,
      requiresManagerOverride: false,
      requiresOwnerOverride: false,
    },
    override: {
      present: false,
      managerName: null,
      reasonCode: null,
      explanation: null,
    },
    deposit: {
      closingFloatMinor: OPENING_FLOAT_MINOR,
      depositAmountMinor: 0,
    },
    tolerance: DEFAULT_TOLERANCE_POLICY,
    zReportPayload: null,
  };
}

export const SEED_DAY_CLOSE_SNAPSHOT: DayCloseSnapshot = buildSnapshot('DEFAULT');

export const SEED_DAY_CLOSE_SNAPSHOT_PREREQ_BLOCKED: DayCloseSnapshot =
  buildSnapshot('PREREQ_BLOCKED');

export const SEED_DEMO_DENOMINATION_COUNTS = SEED_DENOMINATION_COUNTS;
